import React, { useEffect, useState } from 'react';
import Button from './ui/Button';
import Icon from './AppIcon';
import { getAuthHeaders } from '../lib/api';
import { formatMonthYear } from '../../lib/utils/drip-calculator';
import { DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE } from '../../lib/sms/message-formatting.js';

function truncate(text, max = 160) {
  const t = String(text || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Review upcoming monthly messages + first-touch before go-live.
 */
export default function ConfirmTouchpointsOnboarding({
  onComplete,
  firstTouchIntro,
  firstTouchImageUrl,
}) {
  const [loading, setLoading] = useState(true);
  const [touchpoints, setTouchpoints] = useState([]);
  const [error, setError] = useState(null);

  const intro = firstTouchIntro || DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const tpRes = await fetch('/api/touchpoints/view', { headers });
      if (tpRes.ok) {
        const data = await tpRes.json();
        const list = data.data || data.touchpoints || [];
        setTouchpoints(Array.isArray(list) ? list : []);
        if (!list?.length) {
          setError('Still writing your messages…');
        }
      } else {
        setTouchpoints([]);
        setError('Still writing your messages…');
      }
    } catch (err) {
      console.warn('Confirm touchpoints load failed:', err);
      setError('Still writing your messages…');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = [...touchpoints]
    .filter((tp) => tp?.messageText || tp?.message_text)
    .sort((a, b) =>
      String(a.monthYear || a.month_year || '').localeCompare(
        String(b.monthYear || b.month_year || '')
      )
    );

  return (
    <div className="max-w-lg mx-auto text-center p-6">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="MessageSquare" size={32} className="text-primary" />
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">Confirm your upcoming messages</h2>
      <p className="text-muted-foreground mb-6 text-sm">
        These go out on your monthly schedule once messaging is on. Past clients who haven&apos;t
        gotten a text from you yet get your intro instead of the first month&apos;s tip.
      </p>

      {loading ? (
        <div className="py-8 text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Icon name="Loader2" size={18} className="animate-spin" />
          Loading your messages…
        </div>
      ) : (
        <div className="text-left space-y-3 mb-6">
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              First-touch intro
            </p>
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {truncate(intro, 220)}
            </p>
            {firstTouchImageUrl && (
              <img
                src={firstTouchImageUrl}
                alt=""
                className="mt-2 w-16 h-16 rounded-md object-cover border border-border"
              />
            )}
          </div>

          {sorted.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              {error || 'Still writing your messages…'}{' '}
              <button type="button" onClick={load} className="underline font-medium">
                Refresh
              </button>
            </div>
          ) : (
            sorted.map((tp) => {
              const my = tp.monthYear || tp.month_year;
              const text = tp.messageText || tp.message_text || '';
              const label = my ? formatMonthYear(my) : 'Month';
              return (
                <div key={tp.id || my} className="rounded-lg border border-border bg-card p-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    {label}
                    {tp.isCurrentMonth ? ' · current' : ''}
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{truncate(text, 200)}</p>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="space-y-3">
        <Button
          onClick={() => onComplete?.({ confirmed: true })}
          className="w-full"
          disabled={loading}
        >
          Looks good
        </Button>
        <button
          type="button"
          onClick={() => onComplete?.({ confirmed: false, skipped: true })}
          className="w-full text-sm text-muted-foreground hover:text-foreground"
        >
          Skip for now
        </button>
        <p className="text-xs text-muted-foreground">
          Not recommended — give these a quick read so nothing surprising goes out. You can edit
          anytime on Touchpoints.
        </p>
      </div>
    </div>
  );
}
